import React, { useState, useRef, useEffect } from "react";
import api from "../../connector/api";
import { Book, Calendar, FileText, Download, Eye, FileSpreadsheet, BarChart3, RefreshCw } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/common/AppLagout";

/* Build query string, remove empty filters, add format */
const buildQuery = (filters, format) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null) params.append(k, v);
  });
  if (format) params.append("format", format);
  return params.toString();
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const Reports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewType, setPreviewType] = useState(null);

  const [filters, setFilters] = useState({
    books: { author: "", category: "", available: "" },
    patrons: { active: "", fromDate: "", toDate: "" },
    loans: { fromDate: "", toDate: "", status: "" },
  });

  const handleFilter = (type, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  // 1. Header Search State Variables
  const searchRef = useRef(null);
  const [headerSearchTerm, setHeaderSearchTerm] = useState("");
  const debouncedHeaderSearchTerm = useDebounce(headerSearchTerm, 400);
  const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
  const [headerSearchResults, setHeaderSearchResults] = useState([]);
  const [headerShowDropdown, setHeaderShowDropdown] = useState(false);
  const [headerHighlightedIndex, setHeaderHighlightedIndex] = useState(-1);

  const getCoverUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const base = api.defaults.baseURL
            ? api.defaults.baseURL.replace(/\/api\/?$/, "")
            : "";
        const normalizedPath = path.startsWith("/") ? path : `/${path}`;
        return `${base}${normalizedPath}`;
    };


  // 2. Fetch books when typing in the header
  useEffect(() => {
        if (!headerSearchTerm.trim()) {
            setHeaderSearchResults([]);
            setHeaderShowDropdown(false);
            return;
        }

        setHeaderSearchLoading(true);
        const handler = setTimeout(async () => {
            try {
                const res = await api.get("/cataloging/dashboard/search", {
                    params: { q: headerSearchTerm },
                });
                const data = res.data || [];
                const normalizedResults = data.map((b) => ({
                    ...b,
                    cover_url: getCoverUrl(b.cover_image),
                    is_available: b.status === "available",
                }));
                setHeaderSearchResults(normalizedResults);
                setHeaderShowDropdown(true);
            } catch (err) {
                console.error("Header search failed:", err);
                setHeaderSearchResults([]);
            } finally {
                setHeaderSearchLoading(false);
            }
        }, 400);

        return () => clearTimeout(handler);
    }, [headerSearchTerm]);

  const handleSelectHeaderBook = (book) => {
    setHeaderShowDropdown(false);
    setHeaderSearchTerm("");
    setHeaderHighlightedIndex(-1);
    navigate(`/library/cataloging/book/detail/${book.id}`);
  };

  /* Preview report */
  const previewReport = async (type) => {
    try {
      setLoading(true);
      setPreviewType(type);
      setPreviewData([]);

      const query = buildQuery(filters[type]);
      const res = await api.get(`/library/reports/${type}/preview/?${query}`);
      setPreviewData(res.data || []);
      toast.success("Preview generated successfully");
    } catch {
      toast.error("Failed to generate report preview.");
    } finally {
      setLoading(false);
    }
  };

  /* Export CSV or PDF */
  const exportReport = async (type, format) => {
    try {
      setLoading(true);
      const query = buildQuery(filters[type], format);
      const res = await api.get(`/library/reports/${type}/?${query}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_report_${new Date().toISOString().split("T")[0]}.${format}`;
      a.click();

      toast.success(`Exported ${format.toUpperCase()} dataset`);
    } catch {
      toast.error("Export operation failed.");
    } finally {
      setLoading(false);
    }
  };

  /* Compute totals for numeric columns */
  const computeTotals = () => {
    if (!previewData.length) return {};
    const totals = {};
    Object.keys(previewData[0]).forEach((key) => {
      const allValues = previewData.map((row) => row[key]);
      if (allValues.every((v) => !isNaN(parseFloat(v)) && v !== null && v !== "")) {
        totals[key] = allValues.reduce((a, b) => a + parseFloat(b), 0);
      }
    });
    return totals;
  };

  const totals = computeTotals();

  return (
    <AppLayout
      headerProps={{
        title: "Reports & Exports",
        searchTerm: headerSearchTerm,
        setSearchTerm: setHeaderSearchTerm,
        searchRef,
        searchLoading: headerSearchLoading,
        searchResults: headerSearchResults,
        showDropdown: headerShowDropdown,
        setShowDropdown: setHeaderShowDropdown,
        highlightedIndex: headerHighlightedIndex,
        setHighlightedIndex: setHeaderHighlightedIndex,
        handleSelectBook: handleSelectHeaderBook,
      }}
    >
      <Toaster position="top-right" />

      <div className="space-y-6 font-dynamic-title p-6 bg-[#120f0d] text-stone-200 min-h-screen">
        {/* PAGE TITLE BAR */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-[#2e2720]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-stone-100 tracking-tight">System Data & Exports Ledger</h1>
              <p className="text-xs text-stone-400 mt-0.5">Generate analytical manifests, preview database tables, and export system metrics</p>
            </div>
          </div>
        </div>

        {/* REPORT CONFIGURATION CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ReportCard
            title="Books Asset Report"
            icon={<Book className="text-amber-400 w-4 h-4" />}
            filters={[
              ["Author", "text", filters.books.author, (v) => handleFilter("books", "author", v), "Filter by author name..."],
              ["Category", "text", filters.books.category, (v) => handleFilter("books", "category", v), "Filter by category..."],
              ["Availability", "select", filters.books.available, (v) => handleFilter("books", "available", v), "",
                [["", "All Statuses"], ["true", "Available"], ["false", "Unavailable"]]
              ],
            ]}
            preview={() => previewReport("books")}
            exportCsv={() => exportReport("books", "csv")}
            exportPdf={() => exportReport("books", "pdf")}
            loading={loading && previewType === "books"}
          />

          <ReportCard
            title="Circulation & Loans Report"
            icon={<Calendar className="text-amber-400 w-4 h-4" />}
            filters={[
              ["From Date", "date", filters.loans.fromDate, (v) => handleFilter("loans", "fromDate", v)],
              ["To Date", "date", filters.loans.toDate, (v) => handleFilter("loans", "toDate", v)],
              ["Loan Status", "select", filters.loans.status, (v) => handleFilter("loans", "status", v), "",
                [["", "All Statuses"], ["issued", "Issued / Active"], ["returned", "Returned"], ["overdue", "Overdue"]]
              ],
            ]}
            preview={() => previewReport("loans")}
            exportCsv={() => exportReport("loans", "csv")}
            exportPdf={() => exportReport("loans", "pdf")}
            loading={loading && previewType === "loans"}
          />
        </div>

        {/* PREVIEW LEDGER CONTAINER */}
        <div className="bg-[#1a1613] rounded-2xl border border-[#2e2720] shadow-xl overflow-hidden flex flex-col min-h-[380px]">
          <div className="p-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-400" />
              <h2 className="text-xs font-bold text-stone-200 uppercase tracking-wider">
                {previewType ? `${previewType.toUpperCase()} Manifest Preview` : "Report Data Matrix Preview"}
              </h2>
            </div>
            {previewData.length > 0 && (
              <span className="text-[11px] font-mono text-stone-400 bg-[#1a1613] border border-[#2e2720] px-2.5 py-1 rounded-lg">
                {previewData.length} entries loaded
              </span>
            )}
          </div>

          <div className="p-4 flex-1 overflow-auto max-h-[480px]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-amber-400 gap-3">
                <RefreshCw className="w-6 h-6 animate-spin" />
                <span className="text-xs font-mono font-bold">Querying server database matrix...</span>
              </div>
            ) : previewData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-stone-500 gap-2">
                <FileText className="w-8 h-8 stroke-[1.2]" />
                <p className="text-xs font-bold text-stone-400">No report preview generated.</p>
                <p className="text-[11px] text-stone-500">Select parameter filters above and click Preview to view database rows.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-[#131110] text-stone-400 text-[10px] font-bold uppercase tracking-wider border-b border-[#2e2720]">
                      {Object.keys(previewData[0]).map((k) => (
                        <th key={k} className="p-3 border-r border-[#2e2720] last:border-r-0 whitespace-nowrap">
                          {k.replaceAll("_", " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2e2720] text-stone-300 font-mono">
                    {previewData.map((row, i) => (
                      <tr key={i} className="hover:bg-[#131110]/50 transition-colors">
                        {Object.values(row).map((v, idx) => (
                          <td key={idx} className="p-3 border-r border-[#2e2720] last:border-r-0 whitespace-nowrap text-[11px]">
                            {v !== null && v !== undefined ? String(v) : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {/* Calculated Totals Row */}
                    {Object.keys(totals).length > 0 && (
                      <tr className="bg-[#131110] font-bold text-amber-400 border-t-2 border-[#2e2720]">
                        {Object.keys(previewData[0]).map((k, idx) => (
                          <td key={k} className="p-3 border-r border-[#2e2720] last:border-r-0 whitespace-nowrap text-[11px]">
                            {idx === 0 && totals[k] === undefined ? "SUM TOTALS" : (totals[k] !== undefined ? totals[k].toLocaleString() : "-")}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-[#131110] px-4 py-3 border-t border-[#2e2720] text-[11px] text-stone-500 font-mono">
            Preview displays initial API query limits. Full dataset metrics are included upon CSV/PDF export generation.
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

/* Report Filter Card Sub-Component */
const ReportCard = ({ title, icon, filters, preview, exportCsv, exportPdf, loading }) => (
  <div className="bg-[#1a1613] rounded-2xl border border-[#2e2720] shadow-xl p-5 flex flex-col justify-between">
    <div>
      <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-[#2e2720]">
        <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          {icon}
        </div>
        <h3 className="text-xs font-bold text-stone-100 uppercase tracking-wider">{title}</h3>
      </div>

      <div className="space-y-3 mb-6">
        {filters.map(([label, type, value, onChange, placeholder, options], i) => (
          <div key={i} className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block">{label}</label>
            {type === "select" ? (
              <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#131110] border border-[#2e2720] text-stone-200 px-3 h-9 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono"
              >
                {options.map(([v, t]) => (
                  <option key={v} value={v} className="bg-[#1a1613] text-stone-200">{t}</option>
                ))}
              </select>
            ) : (
              <input
                type={type}
                value={value}
                placeholder={placeholder || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#131110] border border-[#2e2720] text-stone-200 px-3 h-9 rounded-xl text-xs placeholder-stone-600 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono"
              />
            )}
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-3 gap-2 pt-2">
      <button
        onClick={preview}
        disabled={loading}
        className="h-9 bg-[#131110] hover:bg-[#201c17] border border-[#2e2720] text-stone-200 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
      >
        <Eye className="w-3.5 h-3.5 text-stone-400" />
        Preview
      </button>
      <button
        onClick={exportCsv}
        disabled={loading}
        className="h-9 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
      >
        <FileSpreadsheet className="w-3.5 h-3.5" />
        CSV
      </button>
      <button
        onClick={exportPdf}
        disabled={loading}
        className="h-9 bg-amber-500 hover:bg-amber-400 text-stone-950 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm disabled:opacity-50 uppercase tracking-wider"
      >
        <Download className="w-3.5 h-3.5" />
        PDF
      </button>
    </div>
  </div>
);

export default Reports;